/**
 * TeleportArc — the drawn half of VR teleport aiming (XR P2 comfort slice, 2026-09-28): the arc as a string of dots
 * from the move-hand controller and a ring on the landing spot, cyan when the hop is legal, red when it is not.
 *
 * ONE instanced mesh for the dots (a fixed count — a dead dot is a zero scale, so the instance count never moves and
 * no pipeline is rebuilt), one ring + disc for the marker. All in ABSOLUTE world coordinates: these are scene children
 * and the FP64 rebase puts −origin on the scene itself, so a scene child's `position` IS an absolute coordinate.
 * Colours change through the material's colour uniform (no pipeline change). Hidden whenever nobody aims.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';

const DOTS = 40;
const DOT_R = 0.035;
/** Dots closer than this to the hand are not drawn (they would fill the view). */
const NEAR_SKIP_M = 0.6;
const OK_COLOR = 0x5fd8ff;
const BAD_COLOR = 0xff5a4a;

export class TeleportArc {
  readonly group = new THREE.Group();
  private readonly dots: THREE.InstancedMesh;
  private readonly dotMat: THREE.MeshBasicMaterial;
  private readonly ring: THREE.Mesh;
  private readonly disc: THREE.Mesh;
  private readonly markMat: THREE.MeshBasicMaterial;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly s = new THREE.Vector3();
  private readonly p = new THREE.Vector3();
  private shown = 0;

  constructor() {
    this.group.name = 'xr-teleport-arc';
    this.group.visible = false;
    this.dotMat = new THREE.MeshBasicMaterial({ color: OK_COLOR, transparent: true, opacity: 0.9, depthWrite: false });
    markMrtOverlay(this.dotMat);
    const dotGeo = new THREE.SphereGeometry(DOT_R, 8, 6);
    this.dots = new THREE.InstancedMesh(dotGeo, this.dotMat, DOTS);
    this.dots.frustumCulled = false;
    this.dots.renderOrder = 900;
    for (let i = 0; i < DOTS; i++) this.dots.setMatrixAt(i, this.m.makeScale(0, 0, 0));
    this.dots.instanceMatrix.needsUpdate = true;
    this.markMat = new THREE.MeshBasicMaterial({ color: OK_COLOR, transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide });
    markMrtOverlay(this.markMat);
    const ringGeo = new THREE.RingGeometry(0.42, 0.55, 40).rotateX(-Math.PI / 2);
    const discGeo = new THREE.CircleGeometry(0.16, 24).rotateX(-Math.PI / 2);
    this.ring = new THREE.Mesh(ringGeo, this.markMat);
    this.disc = new THREE.Mesh(discGeo, this.markMat);
    for (const o of [this.ring, this.disc]) { o.frustumCulled = false; o.renderOrder = 901; }
    this.group.add(this.dots, this.ring, this.disc);
  }

  /** Draw the arc (flat [x,y,z,…], absolute) and the marker (absolute; null = no landing). */
  show(points: readonly number[], landing: { x: number; y: number; z: number } | null, ok: boolean): void {
    this.group.visible = true;
    const col = ok ? OK_COLOR : BAD_COLOR;
    this.dotMat.color.setHex(col);
    this.markMat.color.setHex(col);
    const n = Math.floor(points.length / 3);
    // The first dots start NEAR_SKIP_M out from the hand: a dot a hand-width from the eye fills half the view.
    let k0 = 0;
    while (k0 < n - 1 && Math.hypot(points[k0 * 3] - points[0], points[k0 * 3 + 1] - points[1], points[k0 * 3 + 2] - points[2]) < NEAR_SKIP_M) k0++;
    // Spread DOTS evenly over the arc's remaining samples (the arc may be longer or shorter than the dot count).
    const want = Math.min(DOTS, n - k0);
    for (let i = 0; i < DOTS; i++) {
      if (i < want) {
        const k = k0 + (want > 1 ? Math.round((i / (want - 1)) * (n - 1 - k0)) : 0);
        this.p.set(points[k * 3], points[k * 3 + 1], points[k * 3 + 2]);
        const sc = 1 - 0.5 * (i / Math.max(1, want - 1));
        this.s.set(sc, sc, sc);
        this.m.compose(this.p, this.q, this.s);
      } else {
        this.m.makeScale(0, 0, 0);
      }
      this.dots.setMatrixAt(i, this.m);
    }
    this.dots.instanceMatrix.needsUpdate = true;
    this.shown = want;
    const onMark = !!landing;
    this.ring.visible = onMark;
    this.disc.visible = onMark;
    if (landing) {
      this.ring.position.set(landing.x, landing.y + 0.04, landing.z);
      this.disc.position.set(landing.x, landing.y + 0.05, landing.z);
    }
  }

  hide(): void {
    this.group.visible = false;
    this.shown = 0;
  }

  get visible(): boolean { return this.group.visible; }
  get dotCount(): number { return this.shown; }

  dispose(): void {
    this.group.parent?.remove(this.group);
    this.dots.geometry.dispose();
    this.ring.geometry.dispose();
    this.disc.geometry.dispose();
    this.dotMat.dispose();
    this.markMat.dispose();
  }
}
